import React, { useCallback, forwardRef } from 'react';
import ContentSection from '@/pages/Products/VpsOciDetailPage/components/ContentSection';
import useLocale from '@/hooks/useLocale';
import * as Styles from './Management.styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  IconChevron,
} from 'gatorcomponents';
import { locale } from '@/utils/locale';
import { useHistory } from 'react-router';
import { formatTimestampDate } from '@/utils/Formatters/formatTimestampDate';
import { useParams } from 'react-router-dom';
import { productsActions } from '@/redux/modules';
import { getIconBySlug } from '@/pages/Products/VpsOciDetailPage/utils/getIconBySlug';
import { backupAnalytics } from '@/analytics';

import { ENABLE_VPS_OCI_MANAGEMENT_SO_APPS } from '@/config/GrowthBook/constants';
import { useFeatureIsOn } from '@growthbook/growthbook-react';

const Management = forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const vpsOci = useSelector(state => state.vpsOci);
  const { management, addons: addonsList, ssh } = (vpsOci && vpsOci.data) || {};
  const { serverIp } = ssh || {};
  const enableVpsOciManagementSoApps = useFeatureIsOn(ENABLE_VPS_OCI_MANAGEMENT_SO_APPS);

  const n8nProducts = useSelector(state => state.products);
  const { n8n: n8nState } = n8nProducts || {};
  const { id: productId } = useParams();

  const { productDetails: productDetailsLocale } = useLocale();
  const { vpsOci: managementLocale } = productDetailsLocale;

  const soAppsRedirect = useCallback(() => {
    history.push(`${locale('routes.products')}${locale('routes.productdetail')}/${productId}${locale('routes.templates')}`);
  }, [history, productId]);

  const backupRedirect = useCallback(() => {
    history.push(`${locale('routes.products')}${locale('routes.productdetail')}/${productId}${locale('routes.backup')}`);
    backupAnalytics.backupButtonClick();
  }, [history, productId]);

  const snapshotRedirect = useCallback(() => {
    history.push(`${locale('routes.products')}${locale('routes.productdetail')}/${productId}${locale('routes.snapshots')}`);
  }, [history, productId]);

  const sshKeysRedirect = useCallback(() => {
    history.push(`${locale('routes.products')}${locale('routes.productdetail')}/${productId}${locale('routes.sshKeys')}`);
  }, [history, productId]);

  const cpanelRedirect = useCallback(() => {
    history.push(`${locale('routes.whmRedirectAccess')}/${productId}`);
  }, [history, productId]);

  const n8nRedirect = useCallback(() => {
    if (!n8nState || !productsActions || !productsActions.n8n) return;

    const openN8N = (ip) => {
      const url = `http://${ip}`;
      window.open(url, '_blank');
    };

    if (n8nState.data && n8nState.hostingId === productId) {
      openN8N(n8nState.data.ip);
    } else {
      dispatch(productsActions.n8n.request({ hostingId: productId, actionType: 'open_n8n', callback: openN8N }));
    }
  }, [n8nState, productId, dispatch]);

  const dockerRedirect = useCallback(() => {
    const url = `http://${serverIp}`;
    window.open(url, '_blank');
  }, [serverIp]);

  const moodleRedirect = useCallback(() => {
    const url = `http://${serverIp}`;
    window.open(url, '_blank');
  }, [serverIp]);

  const nodejsRedirect = useCallback(() => {
    const url = `http://${serverIp}`;
    window.open(url, '_blank');
  }, [serverIp]);

  const checkExistSlug = useCallback(slug => !slug || slug === '' || managementLocale.management.items.findIndex(itemLocale => itemLocale.slug === slug) === -1, [managementLocale.management.items]);

  const getNameItem = useCallback((slug) => {
    if (!slug) return null;
    const item = managementLocale.management.items.find(itemLocale => itemLocale.slug === slug);
    return item ? item.name : '';
  }, [managementLocale.management.items]);

  const getInfoItem = useCallback((item) => {
    if (!item) return null;

    const { slug, info } = item;
    if (!slug && !info) return null;

    if (slug === 'so-apps') {
      const { version } = item;
      const name = (managementLocale.store.details.operationalSystems[info] && managementLocale.store.details.operationalSystems[info].name) || item.info;
      return name ? `${name}${version ? ` ${version}` : ''}` : '';
    }

    if (slug === 'backup' || slug === 'snapshot') {
      const { date } = item;
      if (!date) return info;
      return `${managementLocale.management.items.find(i => i.slug === slug).info} ${formatTimestampDate(date)}`;
    }

    return info;
  }, [managementLocale.store.details.operationalSystems, managementLocale.management.items]);

  const handleClick = useCallback((item) => {
    if (!item) return;

    const { href, slug } = item;

    if (href) {
      window.open(href, '_blank');
      return;
    }

    const slugHandlers = {
      'so-apps': soAppsRedirect,
      backup: backupRedirect,
      snapshot: snapshotRedirect,
      'ssh-keys': sshKeysRedirect,
      docker: dockerRedirect,
      moodle: moodleRedirect,
      'node-js': nodejsRedirect,
      cpanel: cpanelRedirect,
      n8n: n8nRedirect,
    };

    const handler = slugHandlers[slug];
    if (handler) {
      handler();
    }
  }, [soAppsRedirect, backupRedirect, snapshotRedirect, sshKeysRedirect, n8nRedirect, cpanelRedirect, dockerRedirect, moodleRedirect, nodejsRedirect]);

  if ((!management || management.length === 0) && (!addonsList || addonsList.length === 0)) return null;

  let managementList = [
    ...(management || []),
    ...(addonsList || []),
  ];

  if (!enableVpsOciManagementSoApps) {
    managementList = managementList.filter(item => item.slug !== 'so-apps');
  }

  if (managementList.length === 0) return null;

  return (
    <ContentSection
      ref={ref}
      direction="column"
      align="flex-start"
      justify="flex-start"
      title={managementLocale.management.title.text}
      tooltip={managementLocale.management.title.tooltip}
      testId="vps-management-section"
    >
      <Styles.ListItems>
        {managementList
          .filter(item => !checkExistSlug(item.slug))
          .map((item) => {
            const iconItem = getIconBySlug(item.slug);
            const itemName = getNameItem(item.slug);
            const itemInfo = getInfoItem(item);

            return (
              <Styles.Item key={item.slug} onClick={() => handleClick(item)} data-testid={`vps-management-item-${item.slug}`}>
                {iconItem && <Styles.ItemIcon src={iconItem} alt={item.slug} data-testid={`vpsoci-management-icon-${item.slug}`} />}
                <Styles.ItemContent>
                  {itemName && <Styles.ItemName data-testid="vps-management-name">{itemName}</Styles.ItemName>}
                  {itemInfo && <Styles.ItemInfo data-testid="vps-management-info">{itemInfo}</Styles.ItemInfo>}
                </Styles.ItemContent>
                <Styles.ItemArrow>
                  <IconChevron color="primaryMedium" size="lg" direction="right" />
                </Styles.ItemArrow>
              </Styles.Item>
            );
          })}
      </Styles.ListItems>
    </ContentSection>
  );
});

Management.displayName = 'Management';

export default Management;
